import { useCallback, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { getOwnPrivateAppUser } from '@idk-web/api';
import { privateUser, updateUser, type PrivateUser } from '@/state/privateAuth';
import { usePrivateLogin } from '@/hooks/private/login/usePrivateLogin';

export type User = PrivateUser & {
  /**
   * Reloads the current user globally
   */
  reload(): Promise<void>;
};

/**
 * Returns the currently logged in private app user.
 *
 * This hook may only be used in components that are only mounted when a private
 * user is guaranteed to logged in.
 */
export function usePrivateUser(): User {
  const auth = usePrivateLogin();
  const user = useAtomValue(privateUser);
  const reload = useCallback(async () => {
    if (!user) {
      console.warn('Tried to reload user when user is missing in state');
      return;
    }

    const response = await getOwnPrivateAppUser();

    updateUser(response);
  }, [user]);

  useEffect(() => {
    if (!user) {
      void auth.logout();
    }
  }, [user]);

  return useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...user!,
      reload,
    }),
    [user, reload],
  );
}
